/**
 * The title's backdrop (04 §7): an endless field of cave tiles drifting slowly upwards, as if the
 * screen were sinking into the ground, drawn in dim colours with every other line blacked out.
 */

import { atlasFor } from '../../render/atlas';
import { BLACK, BROWN, DARK_GREY, GREY } from '../../render/palette';
import type { Screen } from '../../render/screen';
import type { SpriteName } from '../../render/sprites';
import { CELL, hash } from '../../render/sprites/types';
import { animFrame } from './theme';

export interface Tile {
  readonly sprite: SpriteName;
  readonly frame: number;
}

/** What lies at tile (tx, ty) of the endless field; `anim` is the 25 Hz clock. Pure. */
export function tileAt(tx: number, ty: number, anim: number): Tile | null {
  if (hash(tx >> 2, ty, 9) % 13 === 0 && hash(tx, ty, 2) % 5 !== 0) return { sprite: 'brick', frame: 0 };
  const h = hash(tx, ty, 3) % 100;
  if (h < 11) return { sprite: 'boulder', frame: 0 };
  if (h < 15) return { sprite: 'diamond', frame: (anim >> 1) + (hash(tx, ty, 4) % 8) };
  if (h < 24) return null;
  if (h === 99) return { sprite: hash(tx, ty, 6) % 2 === 0 ? 'firefly' : 'butterfly', frame: anim >> 1 };
  return { sprite: 'dirt', frame: 0 };
}

export class TileField {
  private readonly colors: readonly [number, number, number];

  /** `speed`: pixels per second upwards; `sway`: pixels of slow side-to-side drift. */
  constructor(
    colors: readonly [number, number, number] = [BROWN, DARK_GREY, GREY],
    private readonly speed = 9,
    private readonly sway = 40,
  ) {
    this.colors = colors;
  }

  /** Fill the band [top, bottom) of the screen, between `left` and `right`. */
  draw(screen: Screen, ms: number, top: number, bottom: number, left = 0, right = screen.width, dim = true): void {
    const atlas = atlasFor(this.colors);
    const ctx = screen.ctx;
    const scrollY = Math.floor((ms * this.speed) / 1000);
    const scrollX = Math.round(Math.sin(ms / 9000) * this.sway);
    const anim = animFrame(ms);
    ctx.save();
    ctx.beginPath();
    ctx.rect(left, top, right - left, bottom - top);
    ctx.clip();
    const ty0 = Math.floor(scrollY / CELL);
    const tx0 = Math.floor(scrollX / CELL);
    for (let ty = ty0; top + ty * CELL - scrollY < bottom; ty++) {
      const y = top + ty * CELL - scrollY;
      for (let tx = tx0; tx * CELL - scrollX < right; tx++) {
        const tile = tileAt(tx, ty, anim);
        if (tile) atlas.draw(ctx, tile.sprite, tile.frame, tx * CELL - scrollX, y);
      }
    }
    ctx.restore();
    if (dim) for (let y = top + (top % 2); y < bottom; y += 2) screen.fill(left, y, right - left, 1, BLACK);
  }
}
